import { useEffect, useMemo, useRef, useState } from 'react';
import { useCarrierViewClassic, useCarrierViewUniversal, useCreateChangeRequest, useChangeRequests, useReferenceCarriers, useReferenceLinesOfBusiness, useReferenceStates, useAuditLogs } from '../../hooks/useAdminApi';
import { AuditLogEntry, CarrierViewRequest, CarrierViewRecord, ChangeRequest } from '../../api/admin-api';
import { useApi } from '../../api';
import { useAuthContext } from '../../contexts/AuthContextProvider';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { AdminPagination, StatusBadge } from './AdminUi';
import { PreviewPanel } from './ChangeRequestPreview';

type View = 'classic' | 'universal';
type HistoryTarget = { carrierName: string; appetiteId: number; cbcId: number };

const targetTableFor = (view: View) =>
    view === 'classic' ? 'CarrierRecommendation' : 'CarrierAppetiteByUkw';

const cbcTableFor = (view: View) =>
    view === 'classic' ? 'CarrierBusinessClass' : 'CarrierBusinessClassByUkw';

const cbcMaxLen = (view: View) => view === 'classic' ? 200 : 500;
const MAX_MATCHING_SELECT = 10000;

function bulkPreviewChangeRequest(targetTable: string, ids: number[], weight: 0 | 1): ChangeRequest {
    const label = weight === 1 ? 'activate' : 'deactivate';
    return {
        id: 0,
        status: 'pending',
        targetTable,
        targetRecordId: null,
        targetRecordIds: ids,
        changeData: { Weight: weight },
        requestedBy: '',
        requestedByEmail: '',
        requestedAt: '',
        reviewedBy: '',
        reviewedByEmail: '',
        reviewedAt: null,
        approvedBy: '',
        approvedByEmail: '',
        approvedAt: null,
        comments: `Bulk ${label} ${ids.length} records`,
        rejectionReason: '',
        appliedAt: null,
        appliedBy: '',
    };
}

function validateCarrierBusinessClass(value: string, maxLen: number): string | null {
    if (!value.trim()) return 'Carrier business class cannot be empty.';
    if (value.trim().length > maxLen) return `Carrier business class must be ${maxLen} characters or fewer.`;
    return null;
}

function collectLockedIds(open: { targetTable: string; targetRecordId: number | null; targetRecordIds: number[] }[] | undefined, table: string) {
    const ids = new Set<number>();
    for (const cr of open ?? []) {
        if (cr.targetTable !== table) continue;
        if (cr.targetRecordId) ids.add(cr.targetRecordId);
        for (const id of cr.targetRecordIds ?? []) ids.add(id);
    }
    return ids;
}

function noticeText(n: { message?: string; description?: string } | undefined) {
    return n?.message || n?.description;
}

function actionErrorMessage(e: unknown) {
    const err = e as { response?: { data?: unknown }; message?: string };
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (Array.isArray(data)) {
        const texts = data.map(item => noticeText(item as { message?: string; description?: string })).filter(Boolean);
        if (texts.length) return texts.join('; ');
    }
    if (data && typeof data === 'object') {
        const o = data as { message?: string; notices?: { message?: string; description?: string }[] };
        const nested = o.notices?.map(noticeText).filter(Boolean).join('; ');
        if (nested) return nested;
        if (o.message) return o.message;
    }
    return err?.message || 'Request failed';
}

export const AppetiteMapping = () => {
    const { adminApi } = useApi();
    const { getUserName, getUserAccount, getAuthToken } = useAuthContext();
    const { canRequestChange } = useAdminAccess();
    const [view, setView] = useState<View>('classic');
    const [params, setParams] = useState<CarrierViewRequest>({
        page: 1, pageSize: 25, sortBy: 'carrierName', sortDirection: 'asc',
    });
    const [carrierFilter, setCarrierFilter] = useState('');
    const [lobFilter, setLobFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionOk, setActionOk] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(null);
    const [bulkConfirmWeight, setBulkConfirmWeight] = useState<0 | 1 | null>(null);
    const [allMatchingSelected, setAllMatchingSelected] = useState(false);
    const [selectingAll, setSelectingAll] = useState(false);
    const [recordCache, setRecordCache] = useState<Map<number, CarrierViewRecord>>(new Map());
    const headerCheckRef = useRef<HTMLInputElement>(null);

    const { data: carriers } = useReferenceCarriers();
    const { data: lobs } = useReferenceLinesOfBusiness();
    const { data: states } = useReferenceStates();
    const query = { ...params, carrierFilter, lobFilter, stateFilter };
    const classic = useCarrierViewClassic(query);
    const universal = useCarrierViewUniversal(query);
    const { data, isLoading } = view === 'classic' ? classic : universal;
    const { data: openCRs } = useChangeRequests('pending,reviewed,approved');

    const createCR = useCreateChangeRequest();
    const table = targetTableFor(view);
    const cbcTable = cbcTableFor(view);
    const lockedIds = useMemo(() => collectLockedIds(openCRs?.data, table), [openCRs, table]);
    const lockedCbcIds = useMemo(() => collectLockedIds(openCRs?.data, cbcTable), [openCRs, cbcTable]);

    const handlePageChange = (newPage: number) => {
        setParams(prev => ({ ...prev, page: newPage }));
        setEditingKey(null);
    };

    const hasFilter = !!(carrierFilter || lobFilter || stateFilter);

    useEffect(() => {
        setSelectedRows([]);
        setAllMatchingSelected(false);
        setRecordCache(new Map());
        setEditingKey(null);
    }, [carrierFilter, lobFilter, stateFilter, view]);

    useEffect(() => {
        if (!data?.data) return;
        setRecordCache(prev => {
            const next = new Map(prev);
            for (const row of data.data) next.set(row.id, row);
            return next;
        });
    }, [data]);

    const submitWeightChange = async (ids: number[], newWeight: number, expectedById: Map<number, number>, comments: string) => {
        setActionError(null);
        setActionOk(null);
        const email = getUserAccount()?.username ?? '';
        const expected = ids.length === 1 ? expectedById.get(ids[0]) : undefined;
        const result = await createCR.mutateAsync({
            targetTable: table,
            targetRecordId: ids.length === 1 ? ids[0] : undefined,
            targetRecordIds: ids.length > 1 ? ids : undefined,
            changeData: {
                Weight: newWeight,
                ...(expected !== undefined ? { ExpectedWeight: expected } : {}),
            },
            requestedBy: getUserName() ?? 'Agent',
            requestedByEmail: email,
            comments,
            autoSubmit: true,
        });
        if (!result.success)
            throw new Error(result.message || 'Change request was not created');
        return result;
    };

    const handleToggleWeight = async (record: CarrierViewRecord) => {
        if (lockedIds.has(record.id)) return;
        const newWeight = record.weight === 1 ? 0 : 1;
        setBusyId(record.id);
        try {
            await submitWeightChange(
                [record.id],
                newWeight,
                new Map([[record.id, record.weight]]),
                `Toggle weight from ${record.weight} to ${newWeight}`,
            );
            setActionOk(`CR submitted to ${newWeight === 1 ? 'activate' : 'deactivate'} this row. It will change after approval and apply.`);
        } catch (e) {
            setActionError(actionErrorMessage(e));
        } finally {
            setBusyId(null);
        }
    };

    const handleBulkToggle = async (newWeight: 0 | 1) => {
        if (selectedRows.length === 0) return;
        const label = newWeight === 1 ? 'activate' : 'deactivate';
        const count = selectedRows.length;
        try {
            const expectedById = new Map(selectedRows
                .map(id => recordCache.get(id))
                .filter((row): row is CarrierViewRecord => !!row)
                .map(row => [row.id, row.weight]));
            await submitWeightChange(
                selectedRows,
                newWeight,
                expectedById,
                `Bulk ${label} ${count} records`,
            );
            setSelectedRows([]);
            setAllMatchingSelected(false);
            setBulkConfirmWeight(null);
            setActionOk(`CR submitted to ${label} ${count} row(s).`);
        } catch (e) {
            setActionError(actionErrorMessage(e));
        }
    };

    const toggleRowSelection = (id: number) => {
        if (lockedIds.has(id)) return;
        setAllMatchingSelected(false);
        setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    };

    const unlockedOnPage = (data?.data ?? []).filter(r => !lockedIds.has(r.id));
    const allOnPageSelected = unlockedOnPage.length > 0 && unlockedOnPage.every(r => selectedRows.includes(r.id));
    const someOnPageSelected = unlockedOnPage.some(r => selectedRows.includes(r.id));

    useEffect(() => {
        if (!headerCheckRef.current) return;
        headerCheckRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }, [someOnPageSelected, allOnPageSelected]);

    const toggleAll = () => {
        const unlockedIds = unlockedOnPage.map(r => r.id);
        if (allMatchingSelected || allOnPageSelected) {
            setSelectedRows(allMatchingSelected ? [] : prev => prev.filter(id => !unlockedIds.includes(id)));
            setAllMatchingSelected(false);
            return;
        }
        setSelectedRows(prev => [...new Set([...prev, ...unlockedIds])]);
    };

    const selectAllMatching = async () => {
        if (!hasFilter) {
            setActionError('Choose a carrier, LOB, or state before selecting all matching rows.');
            return;
        }
        const total = data?.totalCount ?? 0;
        if (total === 0) return;
        if (total > MAX_MATCHING_SELECT) {
            setActionError(`More than ${MAX_MATCHING_SELECT} rows match. Narrow the filters.`);
            return;
        }
        setSelectingAll(true);
        setActionError(null);
        try {
            const fetch = view === 'classic' ? adminApi.getCarrierViewClassic : adminApi.getCarrierViewUniversal;
            const result = await fetch({ ...query, page: 1, pageSize: total }, getAuthToken());
            const unlocked = (result.data ?? []).filter(r => !lockedIds.has(r.id));
            setRecordCache(prev => {
                const next = new Map(prev);
                for (const row of unlocked) next.set(row.id, row);
                return next;
            });
            setSelectedRows(unlocked.map(r => r.id));
            setAllMatchingSelected(true);
        } catch (e) {
            setActionError(actionErrorMessage(e));
        } finally {
            setSelectingAll(false);
        }
    };

    const rowKey = (record: CarrierViewRecord) =>
        `${record.id}-${record.stateCode}-${record.lineOfBusiness}-${record.keyword || record.primaryKeyword || ''}-${record.secondaryKeyword || ''}`;

    const handleSaveCbc = async (record: CarrierViewRecord, next: string) => {
        const err = validateCarrierBusinessClass(next, cbcMaxLen(view));
        if (err) {
            setActionError(err);
            return;
        }
        if (!record.carrierBusinessClassId) {
            setActionError('This row has no Carrier Business Class id to update.');
            return;
        }
        if (lockedCbcIds.has(record.carrierBusinessClassId)) return;
        const trimmed = next.trim();
        setBusyId(record.carrierBusinessClassId);
        setActionError(null);
        setActionOk(null);
        try {
            const result = await createCR.mutateAsync({
                targetTable: cbcTable,
                targetRecordId: record.carrierBusinessClassId,
                changeData: view === 'classic' ? { Value: trimmed } : { Description: trimmed },
                requestedBy: getUserName() ?? 'Agent',
                requestedByEmail: getUserAccount()?.username ?? '',
                comments: `Update carrier business class for ${record.carrierName} from "${record.carrierBusinessClass || ''}" to "${trimmed}"`,
                autoSubmit: true,
            });
            if (!result.success)
                throw new Error(result.message || 'Change request was not created');
            setEditingKey(null);
            setActionOk('CR submitted to update carrier business class. It will change after approval and apply.');
        } catch (e) {
            setActionError(actionErrorMessage(e));
        } finally {
            setBusyId(null);
        }
    };

    const switchView = (next: View) => {
        setView(next);
        setSelectedRows([]);
        setEditingKey(null);
        setParams(p => ({ ...p, page: 1 }));
        setActionError(null);
        setActionOk(null);
    };

    const pending = createCR.isPending;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Appetite Mapping</h1>
                    <p className="admin-page-subtitle">
                        Manage carrier appetite weights for{' '}
                        {view === 'classic' ? 'Classic keyword-based' : 'Universal keyword-pair'} product lines.
                        Changes go through the approval workflow before taking effect.
                    </p>
                </div>
            </div>

            <div className="admin-tabs">
                <button type="button" className={`admin-tab${view === 'classic' ? ' active' : ''}`} onClick={() => switchView('classic')}>
                    Classic
                </button>
                <button type="button" className={`admin-tab${view === 'universal' ? ' active' : ''}`} onClick={() => switchView('universal')}>
                    Universal
                </button>
            </div>

            <div className="admin-filters">
                <div className="select-box admin-select-wide">
                    <select value={carrierFilter} onChange={e => { setCarrierFilter(e.target.value); setParams(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Carriers</option>
                        {carriers?.data.map(c => <option key={c.carrierId} value={c.carrierId}>{c.carrierName}</option>)}
                    </select>
                </div>
                <div className="select-box">
                    <select value={lobFilter} onChange={e => { setLobFilter(e.target.value); setParams(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Lines of Business</option>
                        {lobs?.data.map(l => <option key={l.lineOfBusinessId} value={l.lineOfBusinessId}>{l.lobName}</option>)}
                    </select>
                </div>
                <div className="select-box admin-select-sm">
                    <select value={stateFilter} onChange={e => { setStateFilter(e.target.value); setParams(p => ({ ...p, page: 1 })); }}>
                        <option value="">All States</option>
                        {states?.data.map(s => <option key={s.stateCode} value={s.stateCode}>{s.stateName}</option>)}
                    </select>
                </div>
                {canRequestChange && <button
                    type="button"
                    className="admin-btn admin-btn-secondary"
                    disabled={pending || selectingAll || !hasFilter || (data?.totalCount ?? 0) === 0}
                    title={hasFilter ? undefined : 'Choose a carrier, line of business, or state first'}
                    onClick={selectAllMatching}>
                    {selectingAll
                        ? <><span className="admin-spin" /> Selecting…</>
                        : `Select all ${data?.totalCount ?? 0} matching results`}
                </button>}
            </div>

            {actionError && (
                <div className="admin-alert admin-alert-error admin-alert-row" role="alert">
                    <span className="admin-icon icon-alert" aria-hidden="true" />
                    <div className="admin-alert-body">{actionError}</div>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => setActionError(null)}>Dismiss</button>
                </div>
            )}
            {actionOk && (
                <div className="admin-alert admin-alert-ok admin-alert-row" role="status">
                    <span className="admin-icon icon-confirmation" aria-hidden="true" />
                    <div className="admin-alert-body">{actionOk}</div>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => setActionOk(null)}>Dismiss</button>
                </div>
            )}

            {canRequestChange && selectedRows.length > 0 && (
                <div className="admin-banner">
                    <span>
                        {allMatchingSelected
                            ? `All ${selectedRows.length} results matching current filters are selected`
                            : `${selectedRows.length} row${selectedRows.length !== 1 ? 's' : ''} selected`}
                    </span>
                    {!allMatchingSelected && hasFilter && allOnPageSelected && (data?.totalCount ?? 0) > selectedRows.length && (
                        <button type="button" className="admin-link-btn" disabled={selectingAll} onClick={selectAllMatching}>
                            Select all {data?.totalCount} matching results
                        </button>
                    )}
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" disabled={pending} onClick={() => setBulkConfirmWeight(1)}>Activate Selected</button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-secondary" disabled={pending} onClick={() => setBulkConfirmWeight(0)}>Deactivate Selected</button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => { setSelectedRows([]); setAllMatchingSelected(false); }}>Clear Selection</button>
                </div>
            )}

            {isLoading ? (
                <div className="admin-empty">
                    <span className="admin-spin admin-spin-lg" />
                    <p>Loading appetite records…</p>
                </div>
            ) : (
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {canRequestChange && (
                                <th className="admin-check">
                                    <input
                                        ref={headerCheckRef}
                                        type="checkbox"
                                        onChange={toggleAll}
                                        checked={allOnPageSelected}
                                    />
                                </th>
                                )}
                                <th>Carrier</th>
                                <th>Business Class</th>
                                <th>Line of Business</th>
                                <th>State</th>
                                {view === 'classic' ? (
                                    <th>Keyword</th>
                                ) : (
                                    <>
                                        <th>Primary Keyword</th>
                                        <th>Secondary Keyword</th>
                                    </>
                                )}
                                <th>Status</th>
                                <th className="admin-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.data.map(record => {
                                const locked = lockedIds.has(record.id);
                                const cbcLocked = lockedCbcIds.has(record.carrierBusinessClassId);
                                const key = rowKey(record);
                                return (
                                    <tr key={key}>
                                        {canRequestChange && (
                                        <td className="admin-check">
                                            <input type="checkbox" disabled={locked}
                                                checked={selectedRows.includes(record.id)}
                                                onChange={() => toggleRowSelection(record.id)} />
                                        </td>
                                        )}
                                        <td>{record.carrierName}</td>
                                        <td>
                                            <CbcEditor
                                                record={record}
                                                view={view}
                                                editing={editingKey === key}
                                                locked={cbcLocked}
                                                pending={pending || busyId === record.carrierBusinessClassId}
                                                onCancel={() => setEditingKey(null)}
                                                onSave={next => handleSaveCbc(record, next)}
                                            />
                                        </td>
                                        <td>{record.lineOfBusiness}</td>
                                        <td>{record.stateCode}</td>
                                        {view === 'classic' ? (
                                            <td className="admin-keyword">{record.keyword}</td>
                                        ) : (
                                            <>
                                                <td className="admin-keyword">{record.primaryKeyword || '—'}</td>
                                                <td className="admin-keyword">{record.secondaryKeyword || '—'}</td>
                                            </>
                                        )}
                                        <td>
                                            <StatusBadge active={record.weight === 1} />
                                            {locked && <span className="admin-badge admin-badge-warn">Pending CR</span>}
                                        </td>
                                        <td className="admin-actions">
                                            <div className="admin-actions-group" role="group" aria-label="Row actions">
                                                <div className="admin-action-cluster">
                                                    {canRequestChange && (
                                                    <button type="button" className="admin-action-btn"
                                                        disabled={pending || cbcLocked || !record.carrierBusinessClassId || editingKey === key}
                                                        title="Edit the business class label for this record"
                                                        onClick={() => { setActionError(null); setEditingKey(key); }}>
                                                        Edit Class
                                                    </button>
                                                    )}
                                                    {canRequestChange && (
                                                    <button type="button"
                                                        className={`admin-action-btn${record.weight === 1 ? ' admin-action-btn-off' : ' admin-action-btn-on'}`}
                                                        onClick={() => handleToggleWeight(record)}
                                                        disabled={pending || locked || busyId === record.id}
                                                        title={record.weight === 1 ? 'Submit a request to deactivate this appetite entry' : 'Submit a request to activate this appetite entry'}>
                                                        {busyId === record.id
                                                            ? <><span className="admin-spin" /> Submitting…</>
                                                            : record.weight === 1 ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                    )}
                                                    <button type="button" className="admin-action-btn"
                                                        title="View the full change history for this record"
                                                        onClick={() => setHistoryTarget({
                                                            carrierName: record.carrierName,
                                                            appetiteId: record.id,
                                                            cbcId: record.carrierBusinessClassId,
                                                        })}>
                                                        History
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && data && (
                <AdminPagination page={params.page || 1} pageSize={params.pageSize || 25} totalCount={data.totalCount} onPageChange={handlePageChange} />
            )}

            {historyTarget && (
                <HistoryPanel
                    target={historyTarget}
                    appetiteTable={table}
                    cbcTable={cbcTable}
                    onClose={() => setHistoryTarget(null)}
                />
            )}

            {bulkConfirmWeight !== null && (
                <div className="admin-modal-backdrop" onClick={() => !pending && setBulkConfirmWeight(null)}>
                    <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="bulk-confirm-title">
                        <h3 id="bulk-confirm-title">
                            {bulkConfirmWeight === 1 ? 'Activate' : 'Deactivate'} {selectedRows.length} Record{selectedRows.length !== 1 ? 's' : ''}
                        </h3>
                        <PreviewPanel
                            cr={bulkPreviewChangeRequest(table, selectedRows, bulkConfirmWeight)}
                            records={selectedRows.map(id => recordCache.get(id)).filter((row): row is CarrierViewRecord => !!row)}
                        />
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-btn admin-btn-secondary" disabled={pending} onClick={() => setBulkConfirmWeight(null)}>Cancel</button>
                            <button
                                type="button"
                                className="admin-btn admin-btn-primary"
                                disabled={pending}
                                onClick={() => handleBulkToggle(bulkConfirmWeight)}>
                                {pending ? 'Submitting…' : `Confirm ${bulkConfirmWeight === 1 ? 'Activate' : 'Deactivate'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function CbcEditor({
    record, view, editing, locked, pending, onCancel, onSave,
}: {
    record: CarrierViewRecord;
    view: View;
    editing: boolean;
    locked: boolean;
    pending: boolean;
    onCancel: () => void;
    onSave: (value: string) => void;
}) {
    const [draft, setDraft] = useState(record.carrierBusinessClass ?? '');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!editing) return;
        setDraft(record.carrierBusinessClass ?? '');
        setError(null);
    }, [editing, record.carrierBusinessClass]);

    if (!editing) {
        return (
            <>
                {record.carrierBusinessClass || '—'}
                {locked && <> <span className="admin-badge admin-badge-warn">Pending CR</span></>}
            </>
        );
    }

    return (
        <div className="admin-cbc-edit">
            <input className="admin-input" value={draft} autoFocus
                maxLength={cbcMaxLen(view)}
                onChange={e => { setDraft(e.target.value); setError(null); }}
                onKeyDown={e => { if (e.key === 'Escape') onCancel(); }} />
            <div className="admin-inline">
                <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" disabled={pending}
                    onClick={() => {
                        const err = validateCarrierBusinessClass(draft, cbcMaxLen(view));
                        if (err) { setError(err); return; }
                        onSave(draft);
                    }}>
                    Save
                </button>
                <button type="button" className="admin-btn admin-btn-sm admin-btn-secondary" disabled={pending} onClick={onCancel}>Cancel</button>
            </div>
            {error && <div className="admin-field-error">{error}</div>}
        </div>
    );
}

const TABLE_LABELS: Record<string, string> = {
    CarrierRecommendation:      'Classic Appetite',
    CarrierAppetiteByUkw:       'Universal Appetite',
    CarrierBusinessClass:       'Business Class (Classic)',
    CarrierBusinessClassByUkw:  'Business Class (Universal)',
};

function friendlyTable(name: string) {
    return TABLE_LABELS[name] ?? name;
}

function formatAuditValues(raw: string | null | undefined): string {
    if (!raw) return '—';
    try {
        const obj = JSON.parse(raw) as Record<string, unknown>;
        return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ');
    } catch (_e) {
        return raw;
    }
}

function prettyJson(raw: string): string {
    try { return JSON.stringify(JSON.parse(raw), null, 2); }
    catch (_e) { return raw; }
}

function HistoryPanel({
    target, appetiteTable, cbcTable, onClose,
}: {
    target: HistoryTarget;
    appetiteTable: string;
    cbcTable: string;
    onClose: () => void;
}) {
    const [selected, setSelected] = useState<AuditLogEntry | null>(null);
    const appetite = useAuditLogs({ tableName: appetiteTable, recordId: target.appetiteId, page: 1, pageSize: 50 }, true);
    const cbc = useAuditLogs(
        { tableName: cbcTable, recordId: target.cbcId, page: 1, pageSize: 50 },
        target.cbcId > 0,
    );
    const rows = useMemo(() => {
        const all = [...(appetite.data?.data ?? []), ...(cbc.data?.data ?? [])];
        return all.sort((a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime());
    }, [appetite.data, cbc.data]);
    const loading = appetite.isLoading || cbc.isLoading;

    return (
        <div className="admin-modal-backdrop" onClick={onClose}>
            <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
                <h3>Change History</h3>
                <p className="admin-muted">
                    {target.carrierName} · Appetite #{target.appetiteId}
                    {target.cbcId ? ` · Business Class #${target.cbcId}` : ''}
                </p>
                {loading ? (
                    <div className="admin-empty">
                        <span className="admin-spin admin-spin-lg" />
                        <p>Loading history…</p>
                    </div>
                ) : rows.length === 0 ? (
                    <p className="admin-muted admin-detail-block">
                        No applied changes yet. History appears once a change request has been approved and applied to the database.
                    </p>
                ) : (
                    <div className="admin-table-wrap admin-detail-block">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Date &amp; Time</th>
                                    <th>Changed By</th>
                                    <th>Record Type</th>
                                    <th>Operation</th>
                                    <th>Previous Value</th>
                                    <th>New Value</th>
                                    <th>Change Request</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(entry => (
                                    <tr key={`${entry.tableName}-${entry.id}`}>
                                        <td className="admin-nowrap">{entry.performedAt ? new Date(entry.performedAt).toLocaleString() : '—'}</td>
                                        <td>{entry.performedBy || '—'}</td>
                                        <td><span className="admin-badge admin-badge-neutral">{friendlyTable(entry.tableName)}</span></td>
                                        <td><span className={`admin-badge ${entry.action === 'INSERT' ? 'admin-badge-on' : entry.action === 'DELETE' ? 'admin-badge-off' : 'admin-badge-warn'}`}>{entry.action}</span></td>
                                        <td className="admin-truncate" title={formatAuditValues(entry.oldValues)}>{formatAuditValues(entry.oldValues)}</td>
                                        <td className="admin-truncate" title={formatAuditValues(entry.newValues)}>{formatAuditValues(entry.newValues)}</td>
                                        <td>{entry.changeRequestId ? `CR-${entry.changeRequestId}` : '—'}</td>
                                        <td className="admin-actions">
                                            <button type="button" className="admin-action-btn" onClick={() => setSelected(entry)}>View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="admin-modal-actions">
                    <button type="button" className="admin-btn admin-btn-primary" onClick={onClose}>Close</button>
                </div>
            </div>
            {selected && (
                <div className="admin-modal-backdrop" onClick={e => { e.stopPropagation(); setSelected(null); }}>
                    <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
                        <h3>Change Details</h3>
                        <p className="admin-muted">
                            {selected.action} · {friendlyTable(selected.tableName)} · Record #{selected.recordId}
                        </p>
                        <p className="admin-muted">
                            {selected.performedBy} · {selected.performedAt ? new Date(selected.performedAt).toLocaleString() : '—'}
                        </p>
                        {selected.changeRequestId !== null && (
                            <p className="admin-muted">Change request: <strong>CR-{selected.changeRequestId}</strong></p>
                        )}
                        {selected.oldValues && (
                            <div className="admin-detail-block">
                                <p className="admin-meta-label">Previous Values</p>
                                <pre className="admin-pre">{prettyJson(selected.oldValues)}</pre>
                            </div>
                        )}
                        {selected.newValues && (
                            <div className="admin-detail-block">
                                <p className="admin-meta-label">New Values</p>
                                <pre className="admin-pre">{prettyJson(selected.newValues)}</pre>
                            </div>
                        )}
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-btn admin-btn-primary" onClick={() => setSelected(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
